import { Box } from "@mui/material"
import { FilterAltOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { ChangeEvent, FC } from "react"
import { useState } from "react"

import { PriceChangeHistoryTable } from "../components/PriceChangeHistoryTable"

import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput/SearchInput"
import type { PriceChangeSubmission } from "@/graphql/codegen/graphql"
import ReSendAllCustomers from "@/screens/Pricing/components/dialogs/SendDialog/ReSendAllCustomers"
import ReSendIndividual from "@/screens/Pricing/components/dialogs/SendDialog/ReSendIndividual"
import PriceChangeHistoryFilterChips from "@/screens/Pricing/components/filterChips/PriceChangeHistoryFilterChips"
import SellingPriceHistoryFilterDrawer from "@/screens/Pricing/components/filters/SellingPriceHistoryFilterDrawer"
import { PricingDialogs, RecipientType, SELLING_PRICE_FILTER_KEYS } from "@/screens/Pricing/constants"
import { useResendToAllCustomers } from "@/screens/Pricing/hooks/api/useResendToAllCustomers"
import { useReSendToIndividualCustomer } from "@/screens/Pricing/hooks/api/useResendToIndividualCustomer"
import { useReSendToIndividualPerson } from "@/screens/Pricing/hooks/api/useResendToIndividualPerson"
import { useRevertPriceChangeSubmission } from "@/screens/Pricing/hooks/api/useRevertPriceChangeSubmission"
import { usePriceChangeHistoryColumns } from "@/screens/Pricing/hooks/usePriceChangeHistoryColumns"
import { usePriceChangeHistoryList } from "@/screens/Pricing/hooks/usePriceChangeHistoryList"
import { usePricingDialog } from "@/screens/Pricing/hooks/usePricingDialog"
import type {
  ReSendToIndividualSchemaFormValues,
  SendPriceChangeSchemaFormValues,
} from "@/screens/Pricing/schema/SendPriceChangeSchema"

const SellingPriceHistoryContainer: FC = () => {
  const navigate = useNavigate()
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  const {
    priceChangeHistoryItems,
    isFetching,
    searchParams,
    handleSearchChange,
    handleSortingChange,
    handleFilterRemove,
    tableContainerRef,
    sorting,
    handleScroll,
  } = usePriceChangeHistoryList({
    route: "/app/pricing/_layout/history/price-change",
    filterKeys: SELLING_PRICE_FILTER_KEYS,
  })

  const { revertPriceChangeSubmission } = useRevertPriceChangeSubmission()
  const { resendToAllCustomers } = useResendToAllCustomers()
  const { resendToIndividualCustomer } = useReSendToIndividualCustomer()
  const { resendToIndividualPerson } = useReSendToIndividualPerson()

  const {
    isDialogOpen,
    setIsDialogOpen,
    setSelectedSubmissionId,
    selectedSubmissionId,
    setSelectedRecipientType,
    selectedRecipientType,
  } = usePricingDialog()

  const navigateToReviewSubmission = async (submissionId: string) => {
    await navigate({
      to: `/app/pricing/${submissionId}/review`,
    })
  }

  const handleRevertPriceChangeSubmission = async (submissionId?: string) => {
    if (!submissionId) {
      return
    }

    const revertedSubmissionId = await revertPriceChangeSubmission({ submissionId })

    if (revertedSubmissionId) {
      await navigateToReviewSubmission(revertedSubmissionId)
    }
  }

  const handleTableRowClick = async (submissionId: string) => {
    await navigateToReviewSubmission(submissionId)
  }

  const handleClickResendToAllCustomers = (submission: PriceChangeSubmission) => {
    setSelectedSubmissionId(submission.id)
    setIsDialogOpen(PricingDialogs.ReSendSellingPrice)
  }

  const handleClickResendToIndividual = (submission: PriceChangeSubmission, recipientType: RecipientType) => {
    setSelectedSubmissionId(submission.id)
    setSelectedRecipientType(recipientType)
    setIsDialogOpen(PricingDialogs.ReSendToIndividual)
  }

  const handleResendToAllCustomers = async (submissionId: string, data: SendPriceChangeSchemaFormValues) => {
    if (!submissionId) {
      return
    }

    const isSuccess = await resendToAllCustomers({
      submissionId,
      emailNote: data.emailNote,
    })

    if (!isSuccess) {
      return
    }

    handleCloseResendModal()
  }

  const handleReSendToIndividual = async ({
    submissionId,
    data,
  }: {
    submissionId: string
    data: ReSendToIndividualSchemaFormValues
  }) => {
    if (!submissionId) {
      return
    }

    let isSuccess = false

    if (data.recipientType === RecipientType.Contact) {
      isSuccess = await resendToIndividualPerson({
        submissionId,
        contactId: data.contactId as string,
        emailNote: data.emailNote,
      })
    } else {
      isSuccess = await resendToIndividualCustomer({
        submissionId,
        companyId: data.customerId as string,
        emailNote: data.emailNote,
      })
    }

    if (!isSuccess) {
      return
    }

    handleCloseResendModal()
  }

  const handleCloseResendModal = () => {
    setIsDialogOpen(null)
    setSelectedSubmissionId(null)
  }

  const columns = usePriceChangeHistoryColumns({
    onRevert: handleRevertPriceChangeSubmission,
    onResendToAllCustomers: handleClickResendToAllCustomers,
    onResendToIndividual: handleClickResendToIndividual,
  })

  return (
    <Box className='flex h-full flex-col gap-y-6'>
      {/* Search */}
      <Box className='flex items-center gap-x-4'>
        <SearchInput
          placeholder='Search by Change ID, or Description...'
          defaultValue={searchParams.query}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
          className='w-full max-w-[524px]'
          data-testid='price-change-history.list-page.search-field'
        />
        <Button
          appearance='outlined'
          startIcon={<FilterAltOutlined />}
          onClick={() => setIsFilterDrawerOpen(true)}
          data-testid='price-change-history.list-page.filter-button'
        >
          Filter
        </Button>
      </Box>

      <PriceChangeHistoryFilterChips searchParams={searchParams} onClearFilterChip={handleFilterRemove} />

      <Box className='flex min-h-0 flex-1 overflow-y-auto'>
        <PriceChangeHistoryTable
          data={priceChangeHistoryItems}
          loading={isFetching}
          containerRef={tableContainerRef}
          columns={columns}
          sorting={sorting}
          onScroll={handleScroll}
          onSortingChange={handleSortingChange}
          onRowClick={handleTableRowClick}
        />
      </Box>

      <SellingPriceHistoryFilterDrawer showFilterDrawer={isFilterDrawerOpen} toggleDrawer={setIsFilterDrawerOpen} />

      {isDialogOpen === PricingDialogs.ReSendSellingPrice && selectedSubmissionId && (
        <ReSendAllCustomers
          submissionId={selectedSubmissionId}
          onClose={handleCloseResendModal}
          onSuccess={handleResendToAllCustomers}
        />
      )}

      {isDialogOpen === PricingDialogs.ReSendToIndividual && selectedSubmissionId && selectedRecipientType && (
        <ReSendIndividual
          submissionId={selectedSubmissionId}
          selectedRecipientType={selectedRecipientType}
          onClose={handleCloseResendModal}
          onSuccess={handleReSendToIndividual}
        />
      )}
    </Box>
  )
}

export default SellingPriceHistoryContainer
